/**
 * Accreditation standards and learning outcomes, as program-level standards.
 *
 * CAPTE, ACOTE, program and institutional learning outcomes used to live in a
 * separate mock (`lib/mock/categories.ts`) read by Reports, the course page and
 * a second Categories screen. They are standards a program reports against, the
 * same job as NCLEX client needs, so they are standards (dimensions) in the one
 * category tree. The mock stays as the seed they are built from.
 *
 * Decision: every one is program-tier. An author tags a question with the
 * course content category only; the program maps content to these standards,
 * and the director manages and reports on them in Settings.
 */

import { MOCK_CATEGORIES } from "@/lib/mock/categories"

import type { CategoryDimension } from "./dimensions"
import type { ExamCategory } from "./types"

const KIND_LABEL: Record<string, string> = {
  accreditation_standard: "Accreditation standard",
  program_competency: "Program competencies",
  course_learning_outcome: "Learning outcomes",
  local_topic: "Local taxonomy",
}

/** Accreditors belong to one discipline; outcomes (PLO, ILO) belong to every program. */
const PROGRAMS_BY_FRAMEWORK: Record<string, string[]> = {
  capte_2024: ["physical therapy"],
  acote_2023: ["occupational therapy"],
}

export const FRAMEWORK_DIMENSIONS: CategoryDimension[] = MOCK_CATEGORIES.map(
  (framework) => ({
    id: framework.id,
    name: framework.name,
    description: KIND_LABEL[framework.type],
    kind: framework.type,
    tier: "program" as const,
    ...(PROGRAMS_BY_FRAMEWORK[framework.id] ? { programs: PROGRAMS_BY_FRAMEWORK[framework.id] } : {}),
    purpose: "reporting" as const,
    required: false,
    lockedFor: {
      reason: "Set by the program, from its accreditor.",
      unlockedBy: "Program admin",
    },
  }),
)

export const FRAMEWORK_EXAM_CATEGORIES: ExamCategory[] = MOCK_CATEGORIES.flatMap(
  (framework) =>
    framework.nodes.map((node, index) => ({
      id: node.id,
      name: node.name,
      code: node.code,
      description: node.description,
      parentId: node.parentId,
      ...(node.parentId === null ? { dimensionId: framework.id } : {}),
      sortOrder: index,
      isActive: true,
    })),
)

/** One icon per kind of standard, so every screen draws the same glyph for it. */
export function standardKindIcon(kind: string | undefined): string {
  switch (kind) {
    case "licensure":
      return "fa-clipboard-check"
    case "course_learning_outcome":
      return "fa-graduation-cap"
    case "program_competency":
      return "fa-user-graduate"
    case "accreditation_standard":
      return "fa-award"
    default:
      return "fa-tags"
  }
}
